import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { QrCode, AlertCircle, RefreshCw } from "lucide-react";
import useApi from "../../../CustomHooks/useApi";
import useGeoLocation from "../../../CustomHooks/useGeoLocation";

// ── Amount presets ────────────────────────────────────────────────────────────
const PRESETS = [100, 200, 500, 1000, 2000, 5000];

// ── Main Component ────────────────────────────────────────────────────────────
export default function UpiCashout() {
  const mid = useSelector((s) => s.auth.employeeDetails?.username);
  const { location, fetchLocation } = useGeoLocation();

  useEffect(() => { fetchLocation(); }, []);

  // ── API hooks ────────────────────────────────────────────────────────────────
  const { callApi: initiateApi } = useApi("/upicw/fingpay/initiate", "POST");

  // ── State ────────────────────────────────────────────────────────────────────
  const [amount, setAmount]   = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [qrData, setQrData]   = useState(null);
  const [countdown, setCountdown] = useState(0);

  const timerRef = useRef(null);

  // ── Cleanup on unmount ───────────────────────────────────────────────────────
  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  // ── Countdown ticker ─────────────────────────────────────────────────────────
  const startCountdown = (seconds) => {
    clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Validation ───────────────────────────────────────────────────────────────
  const validate = () => {
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt <= 0) return "Enter a valid amount.";
    if (amt < 100)                          return "Minimum amount is ₹100.";
    if (amt > 5000)                         return "Maximum amount is ₹5,000.";
    if (amt % 100 !== 0)                    return "Amount must be in multiples of ₹100.";
    if (!location)                          return "Location access is required. Please allow location and try again.";
    return null;
  };

  // ── Initiate ─────────────────────────────────────────────────────────────────
  const handleInitiate = async () => {
    setError("");
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    try {
      const res = await initiateApi({
        mid,
        amount: Number(amount),
        latitude:  location.lat,
        longitude: location.lng,
        remarks: remarks.trim() || "cash withdrawal",
      });

      if (res?.status === "PENDING" && res?.qrImageBase64) {
        setQrData(res);
        startCountdown(res.qrExpiryIn || 60);
      } else {
        setError(res?.message || "Failed to generate QR. Please try again.");
      }
    } catch (e) {
      setError(
        e?.response?.data?.message ||
        e?.message ||
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Reset for new transaction ─────────────────────────────────────────────────
  const handleReset = () => {
    clearInterval(timerRef.current);
    setQrData(null);
    setAmount("");
    setRemarks("");
    setError("");
    setCountdown(0);
  };

  // ── UI ────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="w-full">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-[#12345A] flex items-center justify-center shrink-0">
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">UPI Cash Withdrawal</h1>
            <p className="text-sm text-gray-500">Generate a UPI QR — customer pays, you hand over cash</p>
          </div>
        </div>

        {/* ── STEP 1: Amount form ─────────────────────────────────────────── */}
        {!qrData && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">

            {/* Amount presets */}
            <p className="text-sm font-semibold text-slate-600 mb-3">Select or enter amount</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAmount(String(p))}
                  className={`py-2 rounded-xl text-sm font-bold border transition-all ${
                    amount === String(p)
                      ? "bg-[#12345A] text-white border-[#12345A]"
                      : "border-slate-200 text-slate-700 hover:border-[#12345A] hover:text-[#12345A]"
                  }`}
                >
                  ₹{p}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-600 block mb-1.5">Amount (₹)</label>
              <input
                type="number"
                placeholder="Enter amount (multiples of ₹100)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min={100}
                max={5000}
                step={100}
                className="w-full border border-slate-300 rounded-xl px-4 py-3 text-lg font-bold text-slate-800 outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition"
              />
              <p className="text-xs text-gray-400 mt-1">₹100 – ₹5,000 · multiples of ₹100</p>
            </div>

            {/* Remarks */}
            <div className="mb-5">
              <label className="text-sm font-medium text-slate-600 block mb-1.5">Remarks (optional)</label>
              <input
                type="text"
                placeholder="e.g. cash withdrawal"
                value={remarks}
                maxLength={100}
                onChange={(e) => setRemarks(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-[#12345A] focus:ring-2 focus:ring-[#12345A]/10 transition"
              />
            </div>

            {/* Location warning */}
            {!location && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl mb-4 text-xs text-amber-700">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                Location access is required. Please allow location in your browser.
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium mb-4">
                {error}
              </div>
            )}

            <button
              onClick={handleInitiate}
              disabled={loading}
              className="w-full py-4 bg-[#12345A] hover:bg-[#0f2d50] text-white font-bold rounded-xl text-base transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" /> Generating QR…
                </>
              ) : (
                <>
                  <QrCode className="w-5 h-5" /> Generate QR Code
                </>
              )}
            </button>
          </div>
        )}

        {/* ── STEP 2: QR Display ─────────────────────────────────────────── */}
        {qrData && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">

            {/* Transaction ID */}
            <div className="mb-5">
              <p className="text-xs text-gray-400 font-medium">Transaction ID</p>
              <p className="text-sm font-mono font-semibold text-slate-700">{qrData.txnId}</p>
            </div>

            {/* QR image */}
            {qrData.qrImageBase64 && (
              <>
                <div className="flex flex-col items-center py-4">
                  <img
                    src={`data:image/png;base64,${qrData.qrImageBase64}`}
                    alt="UPI QR Code"
                    className="w-56 h-56 sm:w-64 sm:h-64 object-contain rounded-xl border border-slate-100 shadow-sm"
                  />
                  {qrData.merchantVpa && (
                    <p className="mt-2 text-xs text-gray-400 font-mono">{qrData.merchantVpa}</p>
                  )}
                </div>

                {/* Countdown */}
                <div className={`mt-2 mb-4 text-center text-sm font-medium ${countdown > 0 ? "text-amber-600" : "text-gray-400"}`}>
                  {countdown > 0
                    ? `QR expires in ${countdown}s`
                    : "QR has expired — payment may still complete if customer already scanned"}
                </div>

                {/* Expiry note */}
                {qrData.qrExpiryNote && (
                  <div className="px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700 mb-4">
                    {qrData.qrExpiryNote}
                  </div>
                )}

                {/* Amount */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl mb-4">
                  <span className="text-sm text-gray-500 font-medium">Amount</span>
                  <span className="text-xl font-bold text-[#12345A]">₹{Number(qrData.amount).toFixed(2)}</span>
                </div>
              </>
            )}

            {/* Action button */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleReset}
                className="flex-1 py-3 bg-[#12345A] hover:bg-[#0f2d50] text-white font-semibold rounded-xl transition text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
